import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@lib/auth';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { ToastContainer } from '@components/ToastContainer';
import { OfflineIndicator } from '@components/OfflineIndicator';
import { AuthenticatedLayout } from '@components/layout/AuthenticatedLayout';
import { AdminLayout } from '@components/layout/AdminLayout';
import { QuickLogModal } from '@components/layout/QuickLogModal';
import { StreakCelebration } from '@components/celebrations/StreakCelebration';
import { featureFlags } from '@lib/featureFlags';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('@pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const LoginLinkVerifyPage = lazy(() => import('@pages/LoginLinkVerifyPage').then((m) => ({ default: m.LoginLinkVerifyPage })));
const ForgotPasswordPage = lazy(() => import('@pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const LogPage = lazy(() => import('@pages/LogPage').then((m) => ({ default: m.LogPage })));
const TeamPage = lazy(() => import('@pages/TeamPage').then((m) => ({ default: m.TeamPage })));
const LeaderboardPage = lazy(() => import('@pages/LeaderboardPage').then((m) => ({ default: m.LeaderboardPage })));
const ScorecardPage = lazy(() => import('@pages/ScorecardPage').then((m) => ({ default: m.ScorecardPage })));
const CommunityEngagementPage = lazy(() => import('@pages/CommunityEngagementPage').then((m) => ({ default: m.CommunityEngagementPage })));
const HistoryPage = lazy(() => import('@pages/HistoryPage').then((m) => ({ default: m.HistoryPage })));
const BadgesPage = lazy(() => import('@pages/BadgesPage').then((m) => ({ default: m.BadgesPage })));
const ChallengesPage = lazy(() => import('@pages/ChallengesPage').then((m) => ({ default: m.ChallengesPage })));
const AnalyticsPage = lazy(() => import('@pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const MessagesPage = lazy(() => import('@pages/MessagesPage').then((m) => ({ default: m.MessagesPage })));
const JournalPage = lazy(() => import('@pages/JournalPage').then((m) => ({ default: m.JournalPage })));
const KanbanBoardPage = lazy(() => import('@pages/KanbanBoardPage').then((m) => ({ default: m.KanbanBoardPage })));
const KanbanWeeklyPage = lazy(() => import('@pages/KanbanWeeklyPage').then((m) => ({ default: m.KanbanWeeklyPage })));
const AdminPage = lazy(() => import('@pages/admin/AdminPage').then((m) => ({ default: m.AdminPage })));
const AdminUsersPage = lazy(() => import('@pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const AdminConfigPage = lazy(() => import('@pages/admin/AdminConfigPage').then((m) => ({ default: m.AdminConfigPage })));
const AdminChallengesPage = lazy(() => import('@pages/admin/AdminChallengesPage').then((m) => ({ default: m.AdminChallengesPage })));
const AdminAuditPage = lazy(() => import('@pages/admin/AdminAuditPage').then((m) => ({ default: m.AdminAuditPage })));
const AdminUserActivitiesPage = lazy(() => import('@pages/admin/AdminUserActivitiesPage').then((m) => ({ default: m.AdminUserActivitiesPage })));
const SalesOSV2Page = lazy(() => import('@pages/SalesOSV2Page').then((m) => ({ default: m.SalesOSV2Page })));
const SalesOSV2OpportunityPage = lazy(() =>
  import('@pages/SalesOSV2OpportunityPage').then((m) => ({ default: m.SalesOSV2OpportunityPage }))
);
const ReportingPage = lazy(() => import('@pages/ReportingPage').then((m) => ({ default: m.ReportingPage })));
const WikiDashboardPage = lazy(() => import('@pages/WikiDashboardPage').then((m) => ({ default: m.WikiDashboardPage })));
const WikiAppPage = lazy(() => import('@pages/WikiAppPage').then((m) => ({ default: m.WikiAppPage })));
const WikiFeatureGroupPage = lazy(() => import('@pages/WikiFeatureGroupPage').then((m) => ({ default: m.WikiFeatureGroupPage })));
const WikiPageView = lazy(() => import('@pages/WikiPageView').then((m) => ({ default: m.WikiPageView })));
const GuidePage = lazy(() => import('@pages/GuidePage').then((m) => ({ default: m.GuidePage })));
const AdminDevGuidePage = lazy(() => import('@pages/admin/AdminDevGuidePage').then((m) => ({ default: m.AdminDevGuidePage })));
const GrowthOverviewPage = lazy(() => import('@pages/growth/GrowthOverviewPage').then((m) => ({ default: m.GrowthOverviewPage })));
const GrowthLinkedInPage = lazy(() => import('@pages/growth/GrowthLinkedInPage').then((m) => ({ default: m.GrowthLinkedInPage })));
const GrowthCommunityPage = lazy(() => import('@pages/growth/GrowthCommunityPage').then((m) => ({ default: m.GrowthCommunityPage })));
const GrowthEmailPage = lazy(() => import('@pages/growth/GrowthEmailPage').then((m) => ({ default: m.GrowthEmailPage })));
const GrowthFinancePage = lazy(() => import('@pages/growth/GrowthFinancePage').then((m) => ({ default: m.GrowthFinancePage })));
const GrowthHealthPage = lazy(() => import('@pages/growth/GrowthHealthPage').then((m) => ({ default: m.GrowthHealthPage })));

// Page loading fallback
function PageLoader(): JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="w-8 h-8 border-4 border-ybh-orange/30 border-t-ybh-orange rounded-full animate-spin" />
    </div>
  );
}

export function App(): JSX.Element {
  const isSalesOSV2Enabled = featureFlags.salesOsV2Enabled;

  return (
    <ErrorBoundary>
      <AuthProvider>
        {/* Skip link for keyboard users */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <OfflineIndicator />
        <StreakCelebration />
        <QuickLogModal />
        <ToastContainer />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/verify" element={<LoginLinkVerifyPage />} />
              <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/kanban" element={<KanbanBoardPage />} />
              <Route path="/kanban/weekly" element={<KanbanWeeklyPage />} />

              {/* Authenticated routes */}
              <Route element={<AuthenticatedLayout />}>
                <Route path="/" element={<Navigate to="/scorecard" replace />} />
                {/* The old Home dashboard is gone; Scorecard is the main page. */}
                <Route path="/dashboard" element={<Navigate to="/scorecard" replace />} />
                <Route path="/log" element={<LogPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/scorecard" element={<ScorecardPage />} />
                <Route path="/community" element={<CommunityEngagementPage />} />
                <Route path="/wiki" element={<WikiDashboardPage />} />
                <Route path="/wiki/pages/:slug" element={<WikiPageView />} />
                <Route path="/wiki/apps/:appSlug" element={<WikiAppPage />} />
                <Route path="/wiki/apps/:appSlug/groups/:groupSlug" element={<WikiFeatureGroupPage />} />
                <Route path="/guide" element={<GuidePage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/badges" element={<BadgesPage />} />
                <Route path="/challenges" element={<ChallengesPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/messages" element={<MessagesPage />} />
                <Route path="/journal" element={<JournalPage />} />
                <Route
                  path="/sales-os-v2"
                  element={isSalesOSV2Enabled ? <SalesOSV2Page /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales-os-v2/overview"
                  element={isSalesOSV2Enabled ? <SalesOSV2Page /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales-os-v2/opportunities"
                  element={isSalesOSV2Enabled ? <SalesOSV2Page /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales-os-v2/scorecard"
                  element={isSalesOSV2Enabled ? <SalesOSV2Page /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales-os-v2/activity"
                  element={isSalesOSV2Enabled ? <SalesOSV2Page /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales"
                  element={isSalesOSV2Enabled ? <Navigate to="/sales/opportunities" replace /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales/overview"
                  element={isSalesOSV2Enabled ? <SalesOSV2Page /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales/opportunities"
                  element={isSalesOSV2Enabled ? <SalesOSV2Page /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales/scorecard"
                  element={isSalesOSV2Enabled ? <SalesOSV2Page /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales/activity"
                  element={isSalesOSV2Enabled ? <SalesOSV2Page /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales-os-v2/opportunities/:opportunityId"
                  element={isSalesOSV2Enabled ? <SalesOSV2OpportunityPage /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/sales/opportunities/:opportunityId"
                  element={isSalesOSV2Enabled ? <SalesOSV2OpportunityPage /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/reporting"
                  element={isSalesOSV2Enabled ? <Navigate to="/reporting/deal-stages" replace /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/reporting/deal-stages"
                  element={isSalesOSV2Enabled ? <ReportingPage /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/reporting/revenue-signed"
                  element={isSalesOSV2Enabled ? <ReportingPage /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/reporting/funnel"
                  element={isSalesOSV2Enabled ? <ReportingPage /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/reporting/activity"
                  element={isSalesOSV2Enabled ? <ReportingPage /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/reporting/forecast"
                  element={isSalesOSV2Enabled ? <ReportingPage /> : <Navigate to="/scorecard" replace />}
                />
                <Route
                  path="/reporting/business-health"
                  element={isSalesOSV2Enabled ? <ReportingPage /> : <Navigate to="/scorecard" replace />}
                />
                {/* Growth dashboard — admin-only, same guard shape as /admin */}
                <Route path="/growth" element={<AdminLayout />}>
                  <Route index element={<GrowthOverviewPage />} />
                  <Route path="linkedin" element={<GrowthLinkedInPage />} />
                  <Route path="community" element={<GrowthCommunityPage />} />
                  <Route path="email" element={<GrowthEmailPage />} />
                  <Route path="finance" element={<GrowthFinancePage />} />
                  <Route path="health" element={<GrowthHealthPage />} />
                </Route>
                {/* Admin routes */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminPage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="users/:userId/activities" element={<AdminUserActivitiesPage />} />
                  <Route path="config" element={<AdminConfigPage />} />
                  <Route path="challenges" element={<AdminChallengesPage />} />
                  <Route path="adjustments" element={<AdminConfigPage />} />
                  <Route path="audit" element={<AdminAuditPage />} />
                  <Route path="guide" element={<AdminDevGuidePage />} />
                </Route>
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/scorecard" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
